import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react';
import { useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Mail } from 'lucide-react';
import { apiFetch } from '@/utils/api';
import { useI18n } from '@/hooks/useI18n';

interface User {
    id: string;
    email: string;
    is_active: boolean;
    is_verified: boolean;
    is_superuser: boolean;
    cost_center_id?: string;
    language: string;
    cost_center?: {
        id: string;
        name: string;
    };
}

interface CostCenter {
    id: string;
    name: string;
    is_active: boolean;
}

interface FormData {
    email: string;
    password: string;
    is_active: boolean;
    is_verified: boolean;
    is_superuser: boolean;
    cost_center_id: string;
    language: string;
}

export default function UserEditPage() {
    const navigate = useNavigate();
  const params = useParams({ strict: false });
  const routerState = useRouterState();
    const { t } = useI18n();
    const { id } = params;
    const isNew = id === 'new';

    const [user, setUser] = useState<User | null>(null);
    const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [sendingEmail, setSendingEmail] = useState(false);
    const [emailSuccess, setEmailSuccess] = useState('');
    const [formData, setFormData] = useState<FormData>({
        email: '',
        password: '',
        is_active: true,
        is_verified: false,
        is_superuser: false,
        cost_center_id: '',
        language: 'en'
    });
    const [error, setError] = useState('');

    useEffect(() => {
        // Router is always ready in TanStack Router
    if (true) {
            loadCostCenters();
            if (!isNew && id) {
                loadUser(id as string);
            } else {
                setLoading(false);
            }
        }
    }, [id, isNew]);

    // Load user data
    const loadUser = async (userId: string) => {
        try {
            const response = await apiFetch(`/api/users/${userId}`);
            if (response.ok) {
                const userData = await response.json();
                setUser(userData);
                setFormData({
                    email: userData.email,
                    password: '', // Don't pre-fill password
                    is_active: userData.is_active,
                    is_verified: userData.is_verified,
                    is_superuser: userData.is_superuser,
                    cost_center_id: userData.cost_center_id || '',
                    language: userData.language || 'en'
                });
            } else {
                const errorData = await response.json();
                setError(errorData.detail || t('users.loadUserFailed', 'admin'));
            }
        } catch (error) {
            console.error('Error loading user:', error);
            setError(t('users.loadUserFailed', 'admin'));
        } finally {
            setLoading(false);
        }
    };

    // Load cost centers
    const loadCostCenters = async () => {
        try {
            const response = await apiFetch('/api/cost-centers/');
            if (response.ok) {
                const data = await response.json();
                setCostCenters(data);
            }
        } catch (error) {
            console.error('Error loading cost centers:', error);
        }
    };

    // Handle form submission
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.email.trim()) {
            setError(t('forms.required', 'common'));
            return;
        }

        if (isNew && !formData.password.trim()) {
            setError(t('users.passwordRequired', 'admin'));
            return;
        }

        setSaving(true);
        setError('');

        try {
            const url = isNew
                ? '/api/users/'
                : `/api/users/${id}`;

            const method = isNew ? 'POST' : 'PATCH';

            // Don't send password if it's empty during edit
            const submitData = { ...formData };
            if (!isNew && !submitData.password) {
                delete (submitData as any).password;
            }

            // Convert empty cost_center_id to null
            if (submitData.cost_center_id === '') {
                (submitData as any).cost_center_id = null;
            }

            const response = await apiFetch(url, {
                method,
                body: JSON.stringify(submitData),
            });

            if (response.ok) {
                navigate({ to: '/admin/users'});
            } else {
                const errorData = await response.json();
                setError(errorData.detail || t('users.saveFailed', 'admin'));
            }
        } catch (error) {
            console.error('Error saving user:', error);
            setError(t('users.saveFailed', 'admin'));
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        navigate({ to: '/admin/users'});
    };

    const handleResendWelcomeEmail = async () => {
        if (!id || isNew) return;
        setSendingEmail(true);
        setError('');
        setEmailSuccess('');
        try {
            const response = await apiFetch(`/api/users/${id}/resend-welcome-email`, {
                method: 'POST',
            });
            if (response.ok) {
                setEmailSuccess(t('users.welcomeEmailSent', 'admin') || 'Welcome email sent successfully. A new temporary password has been generated.');
            } else {
                const errorData = await response.json();
                setError(errorData.detail || t('users.welcomeEmailFailed', 'admin') || 'Failed to send welcome email');
            }
        } catch (error) {
            console.error('Error sending welcome email:', error);
            setError(t('users.welcomeEmailFailed', 'admin') || 'Failed to send welcome email');
        } finally {
            setSendingEmail(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="space-y-6">
                    <div className="flex items-center space-x-4">
                        <Skeleton className="h-8 w-8" />
                        <Skeleton className="h-8 w-48" />
                    </div>
                    <Card>
                        <CardHeader>
                            <Skeleton className="h-6 w-32" />
                            <Skeleton className="h-4 w-64" />
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {[...Array(6)].map((_, i) => (
                                <div key={i} className="space-y-2">
                                    <Skeleton className="h-4 w-24" />
                                    <Skeleton className="h-10 w-full" />
                                </div>
                            ))}
                        </CardContent>
                    </Card>
                </div>
            </Layout>
        );
    }

    if (error && !user && !isNew) {
        return (
            <Layout>
                <div className="text-center py-12">
                    <div className="text-red-600 text-lg font-medium">{error}</div>
                    <p className="text-gray-500 mt-2">
                        {t('users.superuserOrNotFound', 'admin')}
                    </p>
                    <Button
                        variant="outline"
                        onClick={handleCancel}
                        className="mt-4"
                    >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        {t('users.backToUsers', 'admin')}
                    </Button>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex items-center space-x-4">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleCancel}
                        className="h-8 w-8 p-0"
                    >
                        <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight">
                            {isNew ? t('users.createTitle', 'admin') : t('users.editTitle', 'admin')}
                        </h1>
                        <p className="text-muted-foreground">
                            {isNew
                                ? t('users.createSubtitle', 'admin')
                                : t('users.editSubtitle', 'admin', { email: user?.email || t('users.userFallback', 'admin') })
                            }
                        </p>
                    </div>
                </div>

                {error && (
                    <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                {emailSuccess && (
                    <Alert>
                        <AlertDescription>{emailSuccess}</AlertDescription>
                    </Alert>
                )}

                <Card>
                    <CardHeader>
                        <CardTitle>{t('users.cardTitle', 'admin')}</CardTitle>
                        <CardDescription>
                            {isNew
                                ? t('users.cardDescriptionCreate', 'admin')
                                : t('users.cardDescriptionEdit', 'admin')
                            }
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid gap-4">
                                <div className="grid gap-2">
                                    <Label htmlFor="email">{t('forms.email_address', 'common')}</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        placeholder={t('users.emailPlaceholder', 'admin')}
                                        required
                                    />
                                </div>

                                <div className="grid gap-2">
                                    <Label htmlFor="password">
                                        {t('forms.password', 'common')}{' '}
                                        {!isNew && t('users.passwordOptionalNote', 'admin')}
                                    </Label>
                                    <Input
                                        id="password"
                                        type="password"
                                        value={formData.password}
                                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                        placeholder={isNew
                                            ? t('users.passwordPlaceholderNew', 'admin')
                                            : t('users.passwordPlaceholderExisting', 'admin')}
                                        required={isNew}
                                    />
                                </div>

                                <div className="grid gap-2">
                                    <Label htmlFor="cost_center">{t('users.locationCostCenter', 'admin')}</Label>
                                    <Select
                                        value={formData.cost_center_id || "none"}
                                        onValueChange={(value) => setFormData({ ...formData, cost_center_id: value === "none" ? "" : value })}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder={t('users.selectLocation', 'admin')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">{t('settings.account.noLocation', 'common')}</SelectItem>
                                            {costCenters.map((center) => (
                                                <SelectItem key={center.id} value={center.id}>
                                                    {center.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="grid gap-2">
                                    <Label htmlFor="language">{t('users.language', 'admin')}</Label>
                                    <Select
                                        value={formData.language}
                                        onValueChange={(value) => setFormData({ ...formData, language: value })}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder={t('users.selectLanguage', 'admin')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="en">{t('users.languageEnglish', 'admin')}</SelectItem>
                                            <SelectItem value="hu">{t('users.languageHungarian', 'admin')}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="grid gap-4">
                                    <div className="flex items-center space-x-2">
                                        <Checkbox
                                            id="is_active"
                                            checked={formData.is_active}
                                            onCheckedChange={(checked) =>
                                                setFormData({ ...formData, is_active: checked as boolean })
                                            }
                                        />
                                        <Label htmlFor="is_active">{t('users.activeUser', 'admin')}</Label>
                                    </div>

                                    <div className="flex items-center space-x-2">
                                        <Checkbox
                                            id="is_verified"
                                            checked={formData.is_verified}
                                            onCheckedChange={(checked) =>
                                                setFormData({ ...formData, is_verified: checked as boolean })
                                            }
                                        />
                                        <Label htmlFor="is_verified">{t('users.verifiedEmail', 'admin')}</Label>
                                    </div>

                                    <div className="flex items-center space-x-2">
                                        <Checkbox
                                            id="is_superuser"
                                            checked={formData.is_superuser}
                                            onCheckedChange={(checked) =>
                                                setFormData({ ...formData, is_superuser: checked as boolean })
                                            }
                                        />
                                        <Label htmlFor="is_superuser">
                                            {t('users.superuserPrivileges', 'admin')}
                                        </Label>
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-between">
                                {!isNew && (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={handleResendWelcomeEmail}
                                        disabled={sendingEmail || saving}
                                    >
                                        <Mail className="h-4 w-4 mr-2" />
                                        {sendingEmail
                                            ? (t('users.sendingEmail', 'admin') || 'Sending...')
                                            : (t('users.resendWelcomeEmail', 'admin') || 'Resend Welcome Email')}
                                    </Button>
                                )}
                                <div className="flex space-x-4 ml-auto">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={handleCancel}
                                        disabled={saving}
                                    >
                                        {t('actions.cancel', 'common')}
                                    </Button>
                                    <Button
                                        type="submit"
                                        disabled={saving}
                                    >
                                        {saving
                                            ? t('actions.saving', 'common')
                                            : (isNew
                                                ? t('users.createAction', 'admin')
                                                : t('users.updateAction', 'admin'))}
                                    </Button>
                                </div>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
}

export const Route = createFileRoute('/admin/users/$id')({
  component: UserEditPage,
})
